import React, { useEffect, useMemo, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout";
import { PublicRescheduleAPI } from "../../services/publicReschedule.service";
import type { PublicRescheduleSlot, PublicRescheduleView } from "../../services/publicReschedule.service";
import type { RescheduleReason } from "../../services/appointment.service";
import { ApiError } from "../../services/api.errors";

const REASON_OPTIONS: { value: RescheduleReason; label: string }[] = [
  { value: "unwell", label: "I'm unwell" },
  { value: "work_conflict", label: "Work conflict" },
  { value: "transport", label: "Transport issue" },
  { value: "other", label: "Other" },
];

const BLOCKED_MESSAGES: Record<string, string> = {
  SELF_RESCHEDULE_CUTOFF_PASSED:
    "This appointment is too close to reschedule online.",
  SELF_RESCHEDULE_LIMIT_REACHED:
    "You've reached the online reschedule limit for this appointment.",
  SELF_RESCHEDULE_NOT_ALLOWED:
    "This appointment can no longer be rescheduled online.",
};

const TOKEN_STORAGE_KEY = "kaagazy_reschedule_token";

const forgetStoredToken = () => {
  try { sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* storage unavailable */ }
};

// A dead token (unknown/expired, or already used) is useless on refresh too.
const isDeadTokenError = (e: unknown) =>
  e instanceof ApiError && (e.statusCode === 404 || e.details.code === "TOKEN_ALREADY_CONSUMED");

const toDateStr = (iso: string) => iso.slice(0, 10);
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const fmtDateLabel = (dateStr: string) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "short", day: "numeric", month: "short",
  });

const PublicReschedulePage: React.FC = () => {
  // The token rides in the URL fragment, never the query string, so it never
  // hits the frontend host's access logs or the Referer header - mirrors
  // SetPasswordPage's handling of the staff-invite temp password. It's then
  // kept in sessionStorage (this tab only, gone when it closes) before being
  // stripped from the address bar, so a refresh doesn't strand the patient
  // on "missing token" - it's forgotten once used, expired or invalid.
  const [token] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token");
    try {
      if (fromUrl) {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, fromUrl);
        return fromUrl;
      }
      return sessionStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    } catch {
      return fromUrl ?? "";
    }
  });

  useEffect(() => {
    if (token) window.history.replaceState({}, "", "/reschedule");
  }, [token]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<PublicRescheduleView | null>(null);

  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [reason, setReason] = useState<RescheduleReason | "">("");
  const [reasonNote, setReasonNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmedTime, setConfirmedTime] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setLoading(false); setLoadError("This link is missing its token."); return; }
    PublicRescheduleAPI.getView(token)
      .then(setView)
      .catch((e) => {
        if (isDeadTokenError(e)) forgetStoredToken();
        setLoadError(e instanceof ApiError ? e.message : "Could not load this link.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const grouped = useMemo(() => {
    const map: Record<string, PublicRescheduleSlot[]> = {};
    for (const s of view?.available_slots ?? []) {
      const d = toDateStr(s.start_time);
      if (!map[d]) map[d] = [];
      map[d].push(s);
    }
    return map;
  }, [view]);

  const handleConfirm = async () => {
    if (!selectedSlotId || !reason) return;
    if (reason === "other" && !reasonNote.trim()) {
      setSubmitError("Please add a short note for \"Other\".");
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await PublicRescheduleAPI.confirm(token, selectedSlotId, reason, reasonNote || undefined);
      forgetStoredToken(); // single-use - consumed by this reschedule
      setConfirmedTime(result.new_start_time);
    } catch (e) {
      if (isDeadTokenError(e)) forgetStoredToken();
      setSubmitError(e instanceof ApiError ? e.message : "Failed to reschedule. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Reschedule Appointment">
        <p className="text-sm text-slate-400">Loading your appointment…</p>
      </AuthLayout>
    );
  }

  if (loadError || !view) {
    return (
      <AuthLayout title="Reschedule Appointment">
        <p className="text-sm text-red-600">{loadError ?? "Something went wrong."}</p>
      </AuthLayout>
    );
  }

  if (confirmedTime) {
    return (
      <AuthLayout title="Appointment Rescheduled">
        <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3">
          <p className="text-sm text-green-800">
            Your appointment has been moved to{" "}
            <span className="font-semibold">
              {new Date(confirmedTime).toLocaleString("en-IN", {
                weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
              })}
            </span>.
          </p>
        </div>
        <p className="text-xs text-slate-400 mt-3">You'll receive a fresh confirmation shortly.</p>
      </AuthLayout>
    );
  }

  const { appointment } = view;

  if (!view.can_reschedule) {
    const message = BLOCKED_MESSAGES[view.reason_code ?? ""] ?? "This appointment can't be rescheduled online right now.";
    return (
      <AuthLayout title="Reschedule Appointment" subtitle={`Hi ${appointment.patient_first_name || "there"}`}>
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 space-y-2">
          <p className="text-sm text-amber-800">{message}</p>
          <p className="text-sm text-amber-800">
            Please call {appointment.clinic_name || "the clinic"}
            {appointment.clinic_phone && (
              <> at <a href={`tel:${appointment.clinic_phone}`} className="font-semibold underline">{appointment.clinic_phone}</a></>
            )}{" "}
            to reschedule.
          </p>
        </div>
        <div className="mt-4 text-xs text-slate-500">
          Current appointment: {new Date(appointment.current_start_time).toLocaleString("en-IN", {
            weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
          })} with Dr. {appointment.doctor_name}
        </div>
      </AuthLayout>
    );
  }

  const dates = Object.keys(grouped).sort();

  return (
    <AuthLayout title="Reschedule Appointment" subtitle={`Hi ${appointment.patient_first_name || "there"}, pick a new time`}>
      <div className="text-sm text-slate-600 mb-4">
        Currently: <span className="font-medium text-slate-800">
          {new Date(appointment.current_start_time).toLocaleString("en-IN", {
            weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
          })}
        </span> with Dr. {appointment.doctor_name}
      </div>

      {dates.length === 0 ? (
        <p className="text-sm text-slate-400">No available slots in the next 14 days. Please call the clinic.</p>
      ) : (
        <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
          {dates.map((dateStr) => (
            <div key={dateStr}>
              <p className="text-xs font-semibold text-slate-500 mb-1.5">{fmtDateLabel(dateStr)}</p>
              <div className="grid grid-cols-3 gap-2">
                {grouped[dateStr].map((slot) => {
                  const selected = selectedSlotId === slot.slot_id;
                  return (
                    <button
                      key={slot.slot_id}
                      onClick={() => setSelectedSlotId(slot.slot_id)}
                      className={`rounded-lg border px-2 py-2 text-sm transition ${
                        selected
                          ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                          : "border-slate-200 hover:border-cyan-300 text-slate-700"
                      }`}
                    >
                      {fmtTime(slot.start_time)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        {/* Required - it's recorded on the reschedule history (NOT NULL), and
            the confirm button stays disabled until one is picked. */}
        <label htmlFor="reschedule-reason" className="block text-xs font-medium text-slate-500 mb-1">Reason for rescheduling</label>
        <select
          id="reschedule-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value as RescheduleReason)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
        >
          <option value="">Select a reason…</option>
          {REASON_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {reason === "other" && (
        <textarea
          value={reasonNote}
          onChange={(e) => setReasonNote(e.target.value)}
          rows={2}
          placeholder="Add a short note…"
          className="w-full mt-2 rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400"
        />
      )}

      {submitError && <p className="text-sm text-red-600 mt-3">{submitError}</p>}

      <button
        onClick={handleConfirm}
        disabled={!selectedSlotId || !reason || submitting}
        className="w-full mt-4 bg-cyan-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-cyan-600 disabled:opacity-40 transition"
      >
        {submitting ? "Rescheduling…" : "Confirm New Time"}
      </button>
    </AuthLayout>
  );
};

export default PublicReschedulePage;
